/**
 * ColorPickerButton View Component
 *
 * A single color picker button that displays a circular color swatch.
 * Used for selecting tag colors in the UnifiedTagModal.
 * @module features/player-kanban/components/ColorPickerButton
 */

import { memo } from "react";

import { type KanbanColors } from "@/features/player-kanban/constants/colors";

import { Pressable } from "@/components/atoms";

// design-system: dynamic-style waiver — data-driven swatch colour; passed as a
// single style reference so no inline object literal renders on a JSX element.
const bgStyle = (color: string) => ({ backgroundColor: color });

/**
 * Props for ColorPickerButtonView component
 */
interface ColorPickerButtonViewProps {
  readonly color: string;
  readonly isSelected: boolean;
  readonly accessibilityLabel: string;
  readonly onPress: () => void;
}

/**
 * Single color picker button display
 * @param root0 - Component props
 * @param root0.color - The color value this button represents
 * @param root0.isSelected - Whether this color is currently selected
 * @param root0.accessibilityLabel - Accessibility label for the button
 * @param root0.onPress - Callback when button is pressed
 */
const ColorPickerButtonView = ({
  color,
  isSelected,
  accessibilityLabel,
  onPress,
}: ColorPickerButtonViewProps) => (
  /* design-system: dynamic-style waiver — data-driven value (swatch color) */
  <Pressable
    onPress={onPress}
    className={`size-8 rounded-full ${
      isSelected
        ? "border-2 border-accent-primary"
        : "border border-outline-subtle"
    }`}
    // eslint-disable-next-line no-restricted-syntax -- data-driven swatch colour from `color` prop (KanbanColors); no closed color token
    UNSAFE_style={bgStyle(color)}
    accessible
    accessibilityLabel={accessibilityLabel}
    accessibilityRole="button"
  />
);

ColorPickerButtonView.displayName = "ColorPickerButtonView";

export default memo(ColorPickerButtonView);

export type { ColorPickerButtonViewProps, KanbanColors };
